import app from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';

type TFirebaseConfig = {
  apiKey: string | undefined;
  authDomain: string | undefined;
  projectId: string | undefined;
  storageBucket: string | undefined;
  messagingSenderId: string | undefined;
  appId: string | undefined;
};

type TUserData = {
  token: string;
  app: string;
  userId: string;
  shop: string;
  installedAt: app.firestore.Timestamp;
};

type TShopDoc = {
  installedAt: app.firestore.Timestamp;
  scope: string;
  tokenVersion: number;
};

// Set Firebase config variables
const config: TFirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

class Firebase {
  private auth: app.auth.Auth;
  private db: app.firestore.Firestore;
  private authPersistence: string;
  public userData: Partial<TUserData>;

  constructor(incognitoMode: boolean) {
    if (!app.apps.length) {
      app.initializeApp(config);
    }

    // Firebase APIs
    this.auth = app.auth();
    this.db = app.firestore();

    // determine the persistence mode based on browser mode
    if (incognitoMode === false) {
      this.authPersistence = app.auth.Auth.Persistence.SESSION;
    } else {
      this.authPersistence = app.auth.Auth.Persistence.NONE;
    }

    // init user data
    this.userData = {}; 
  }

  // User data store API
  setUserData = (idTokenResult: app.auth.IdTokenResult, installedAt: app.firestore.Timestamp) => {
    this.userData = {
      token: idTokenResult.token,
      app: idTokenResult.claims.aud,
      shop: idTokenResult.claims.sfy_shop,
      userId: idTokenResult.claims.sfy_user.id,
      installedAt,
    };
  };

  // Authenticate with firebase
  authenticate = async (customToken: string) => {
    try {
      await this.auth.setPersistence(this.authPersistence);

      // Wait for the sign in to firebase using the received custom token
      await this.auth.signInWithCustomToken(customToken);
      if (!this.auth.currentUser) throw new Error(`Unable to login`);

      // Wait for the user data
      const idTokenResult = await this.auth.currentUser.getIdTokenResult();

      // We need to also get the storefront api key and save it to the user data store
      const shopDoc = await this.shop(idTokenResult.claims.sfy_shop).get();
      if (!shopDoc.exists) throw new Error(`Shop document does not exist`);

      // get data
      const shopData = shopDoc.data() as TShopDoc;
      const { installedAt } = shopData;

      // Set the user data to data store
      this.setUserData(idTokenResult, installedAt);

      return true;
    } catch (error) {
      console.error('Firebase auth error:', error);
      return false;
    }
  };

  // Shop API
  shop = (id: string) => this.db.doc(`sfyShops/${id}`);

  specificConfig = (id: string) => this.db.doc(`sfyShops/${this.userData.shop}/config/${id}`);
}

export default Firebase;
